import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { formatDate, formatDateUTC } from '../../utils/Formatters';
import { isTeachersEnabled } from '../../utils/authScopes';
import { hasASQCompleteFeature } from '../../utils/features';
import classNames from 'classnames';
import ASQSectionResults from './ASQSectionResults';
import Modal from 'react-responsive-modal';
import { FollowUpSurvey } from '../followup/FollowUpSurvey';
import moment from 'moment-timezone';

function daysDiff(futureDate) {
  return moment(futureDate).diff(moment(), 'days');
}

const stateToStr = (s, onClickASQLink, withStart = false) => {
  if (!s) return null;
  const startText = s.status === 'pending' ? 'Start Now' : 'Continue';
  const startLink = () => (
    <a data-asq={s.id} className={`cs-txt-link--sp-${s.state} u-underlined`} onClick={onClickASQLink}>
      {startText}
    </a>
  );
  const withConcern = s.hasConcernAnswer;

  switch (s.state) {
    case 'completed': {
      // Check if any section has a valid result (screening data from Brooke's API)
      const hasValidResults = s.sections && s.sections.some(section => section.score && section.score.result);
      const colorClass = hasValidResults ? `cs-txt-link--sp-${s.state}` : 'cs-txt-link--sp-black';

      return (
        <div className={colorClass}>
          <div className='u-txt--titleize'><b>{s.state}</b></div>
          <div>{formatDateUTC(s.dateFinished)}</div>
          {s.respondentName && <div>{s.respondentName}</div>}
          <div><ASQSectionResults survey={s} /></div>
          {withConcern && <div className='cs-txt--sp-refer'>Concern</div>}
        </div>
      );
    }
    case 'overdue':
      return (
        <div className={`cs-txt-link--sp-${s.state}`}>
          <div className='u-txt--titleize'><b>Pending</b></div>
          {s.respondentName && <div>{s.respondentName}</div>}
          {withStart && <div>{startLink()}</div>}
          {withConcern && <div className='cs-txt--sp-refer'>Concern</div>}
        </div>
      );
    case 'archived':
      return (
        <div className={`cs-txt-link--sp-${s.state}`}>
          <div className='u-txt--titleize'><b>{s.state}</b></div>
          {s.respondentName && <div>{s.respondentName}</div>}
          {withConcern && <div className='cs-txt--sp-refer'>Concern</div>}
        </div>
      );
    default: {
      const days = daysDiff(s.nextAsqAgeRangeStartDate);

      return (
        <div className='cs-txt-link--sp-black'>
          <div className='u-txt--titleize'><b>Grows out of ASQ</b></div>
          {days <= 0 && <div>{formatDateUTC(s.nextAsqAgeRangeStartDate)} (Overdue)</div>}
          {days === 1 && <div>{formatDateUTC(s.nextAsqAgeRangeStartDate)} (Today)</div>}
          {days === 2 && <div>{formatDateUTC(s.nextAsqAgeRangeStartDate)} (Tomorrow)</div>}
          {days > 2 && <div>{formatDateUTC(s.nextAsqAgeRangeStartDate)} (in {days} days)</div>}
          {s.respondentName && <div>{s.respondentName}</div>}
          {withConcern && <div className='cs-txt--sp-refer'>Concern</div>}
          {withStart && <div>{startLink()}</div>}
        </div>
      );
    }
  }
}

export default class ASQSurveysRow extends React.Component {
  static propTypes = {
    auth: PropTypes.object.isRequired,
    download: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    player: PropTypes.object.isRequired,
    playerIdx: PropTypes.number.isRequired,
    onPlayerDetails: PropTypes.func.isRequired,
    onASQLink: PropTypes.func.isRequired,
    onDownloadASQReport: PropTypes.func.isRequired,
    meta: PropTypes.object.isRequired,
  }

  constructor(props) {
    super(props);
    this.state = {
      followUpModalOpen: false,
    };
  }

  onClick = (e) => {
    e.stopPropagation();
    const { player, onPlayerDetails, location, download } = this.props;
    if (download.isDownloading) return;
    onPlayerDetails(player.id, player.questionnaire.id, location);
  }

  onClickASQLink = (e) => {
    e.stopPropagation();
    const { player, onASQLink, location, download } = this.props;
    if (download.isDownloading) return;
    onASQLink(player.id, e.target.dataset.asq, location);
  }

  onClickASQPdf = (e) => {
    e.stopPropagation();
    const { player, onDownloadASQReport, download } = this.props;
    if (download.isDownloading) return;
    onDownloadASQReport(player.questionnaire.id);
  }

  onClickFollowUp = (e) => {
    e.stopPropagation();
    this.setState({ followUpModalOpen: true });
  }

  onCloseFollowUpModal = (_e) => {
    this.setState({ followUpModalOpen: false });
  }

  render() {
    const { auth, download, player, playerIdx, meta } = this.props;
    const s = player.questionnaire;
    const t = player.teacherQuestionnaire;
    const clsRow = classNames('o-rtable__row u-pointer', { 'cs-bg--sp-hover': playerIdx % 2 === 0 });
    const clsPDF = classNames('u-margin-left--small cs-txt-link--sp-blue', { 'u-disabled': download.isDownloading });
    const withReport = !isEmpty(player.asqReportType);
    const followUpAssigned = meta.followUpEnabled && player.asqFollowUpEnabled && player.followUp;
    const withFollowUp = followUpAssigned && ((s && s.state === 'completed') || (t && t.state === 'completed'));
    const followUpHasAnswer = player.followUpQuestionnaire && player.followUpQuestionnaire.filled;
    const statusManagementEnabled = player.asqFollowUpStatusManagementEnabled;
    const teacherOnlyNoContactEnabled = player.teacherOnlyNoContactEnabled;
    const followUpComplete = player.followUpQuestionnaire && player.followUpQuestionnaire.followUpComplete;
    const teacherOnlyNoContact = player.followUpQuestionnaire && player.followUpQuestionnaire.teacherOnlyNoContact;
    const parentCompleted = s && s.state === 'completed';
    const teacherCompleted = t && t.state === 'completed';
    const isPurpleStatus = statusManagementEnabled && teacherCompleted && !parentCompleted && !followUpComplete;

    // Determine follow-up link color class based on status management configuration
    const getFollowUpLinkClass = () => {
      if (!followUpHasAnswer) {
        return 'followup-link cs-txt-link--sp-red'; // Red: No answers
      }

      if (!statusManagementEnabled) {
        return 'followup-link followup-link-green'; // Green: Has answers (legacy behavior)
      }

      // Status management enabled
      if (followUpComplete) {
        return 'followup-link followup-link-green'; // Green: Follow-up complete
      }

      if (parentCompleted) {
        return 'followup-link followup-link-yellow'; // Yellow: Parent ASQ completed but not complete
      }

      if (teacherCompleted) {
        if (teacherOnlyNoContactEnabled && teacherOnlyNoContact) {
          return 'followup-link followup-link-orange'; // Orange: Teacher only, no contact needed
        }
        return 'followup-link followup-link-purple'; // Purple: Only Teacher ASQ completed
      }

      return 'followup-link cs-txt-link--sp-red'; // Red: fallback
    };

    return (
      <Fragment>
        <div className={clsRow} onClick={this.onClick}>
          <div className='o-rtable__cell'>
            <div className='u-flex u-flex-column'>
              <b>{player.lastName},</b>
              <div className='u-flex'>
                <span>{player.firstName}</span>
                <a
                  href={`/dashboard/asqs/${player.id}/player/${s.id}`}
                  className='u-margin-left--small'
                  target='_blank'
                  rel='noopener noreferrer'
                  onClick={(e) => e.stopPropagation()}
                >
                  <i className='fa fa-external-link'></i>
                </a>
              </div>
            </div>
          </div>
          {s ?
            <div className='o-rtable__cell o-rtable__cell--date'>
              <div className='u-flex'>
                <span>{formatDate(s.createdAt)}</span>
                {withReport &&
                  <a className={clsPDF} onClick={this.onClickASQPdf}>
                    {download.id === s.id ?
                      <i className='o-spinner-dual'></i> :
                      <i className='fa fa-file-pdf-o'></i>}
                  </a>}
              </div>
              <span className={`cs-txt--${s.type}`}>
                {s.name} {s.age}
              </span>
            </div> :
            <div className='o-rtable__cell o-rtable__cell--date'></div>}
          {isTeachersEnabled(auth) && (
            <div className='o-rtable__cell'>
              {stateToStr(t, this.onClickASQLink, hasASQCompleteFeature(auth))}
            </div>
          )}
          <div className='o-rtable__cell'>
            {stateToStr(s,  this.onClickASQLink)}
          </div>
          {meta.followUpEnabled && (
            <div className='o-rtable__cell followup-cell'>
              {withFollowUp && followUpHasAnswer &&
                <a className={getFollowUpLinkClass()} onClick={this.onClickFollowUp}>
                  View or Edit Follow-Up Report
                </a>
              }
              {withFollowUp && !followUpHasAnswer &&
                <a className='followup-link cs-txt-link--sp-red' onClick={this.onClickFollowUp}>
                  Start Now: Follow-Up Report
                </a>
              }
              {followUpAssigned && !withFollowUp && (
                <span>Pending (No Screening Completed)</span>
              )}
            </div>
          )}
        </div>
        <Modal
          classNames={{
            overlay: 'o-modal__wrap',
            modal: 'o-modal o-modal__min-height row column u-margin-top--large',
            closeButton: 'o-modal__close-with-faq'
          }}
          closeIcon={
            <div className='o-modal__header-actions'>
              <i className='fa fa-times-circle' style={{ fontSize: 26, color: '#31ccff' }}></i>
            </div>
          }
          closeOnOverlayClick={true}
          open={this.state.followUpModalOpen}
          onClose={this.onCloseFollowUpModal}
          center
        >
          <FollowUpSurvey
            questionnaireId={player.questionnaire.id}
            onSave={this.onCloseFollowUpModal}
            auth={auth}
            statusManagementEnabled={statusManagementEnabled}
            teacherOnlyNoContactEnabled={teacherOnlyNoContactEnabled}
            isPurpleStatus={isPurpleStatus}
          />
        </Modal>
      </Fragment>
    );
  }
}
